/**
 * pendingCashRegisterReceipts.js
 *
 * Tiny AsyncStorage-backed queue of cash-register receipts that have not
 * yet been confirmed as printed by the Fiscat / cashier device. Used so
 * that:
 *
 *  1. If a `sendToCashier` call fails (timeout, BT error, user defers),
 *     the QR payload is preserved per-order and can be re-sent later from
 *     the order details screen.
 *  2. Operators can see at a glance how many receipts are still waiting
 *     for the cashier (e.g. via a small badge).
 *
 * The storage shape is intentionally small – a list of:
 *   {
 *     orderCode, orderId,
 *     qrCodeString,
 *     queuedAt,       // ISO timestamp when first queued
 *     attempts,       // total user-level retry attempts so far
 *     lastError,      // string – last technical error message
 *     lastAttemptAt,  // ISO timestamp of last attempt
 *   }
 */

import AsyncStorage from '@react-native-async-storage/async-storage';

const STORAGE_KEY = 'pendingCashRegisterReceipts';
const MAX_QUEUE_SIZE = 200;

const subscribers = new Set();

const safeParse = (raw) => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_error) {
    return [];
  }
};

const notify = (entries) => {
  subscribers.forEach((listener) => {
    try { listener(entries); } catch (_) { /* ignore */ }
  });
};

const writeQueue = async (entries) => {
  const trimmed = entries.slice(-MAX_QUEUE_SIZE);
  await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  notify(trimmed);
  return trimmed;
};

export const getPendingCashRegisterReceipts = async () => {
  const raw = await AsyncStorage.getItem(STORAGE_KEY);
  return safeParse(raw);
};

export const subscribeToPendingCashRegisterReceipts = (listener) => {
  if (typeof listener !== 'function') return () => {};
  subscribers.add(listener);
  // Fire once with current state.
  getPendingCashRegisterReceipts().then(notify).catch(() => {});
  return () => { subscribers.delete(listener); };
};

const findIndex = (entries, {orderId, orderCode}) => {
  return entries.findIndex((entry) => {
    if (orderId && entry.orderId && entry.orderId === orderId) return true;
    if (orderCode && entry.orderCode && entry.orderCode === orderCode) return true;
    return false;
  });
};

/**
 * Insert or update a pending cash register receipt for the given order.
 * Idempotent on (orderId || orderCode).
 */
export const enqueuePendingCashRegisterReceipt = async ({
  orderId,
  orderCode,
  qrCodeString,
  lastError = '',
} = {}) => {
  if (!qrCodeString) return null;

  const entries = await getPendingCashRegisterReceipts();
  const idx = findIndex(entries, {orderId, orderCode});
  const nowIso = new Date().toISOString();

  if (idx >= 0) {
    const previous = entries[idx];
    entries[idx] = {
      ...previous,
      qrCodeString,
      lastError: lastError || previous.lastError || '',
      lastAttemptAt: nowIso,
      attempts: (previous.attempts || 0) + 1,
    };
  } else {
    entries.push({
      orderId: orderId || null,
      orderCode: orderCode || null,
      qrCodeString,
      lastError: lastError || '',
      queuedAt: nowIso,
      lastAttemptAt: nowIso,
      attempts: 1,
    });
  }

  await writeQueue(entries);
  return entries[idx >= 0 ? idx : entries.length - 1];
};

/**
 * Remove a queued receipt – call this on successful send.
 */
export const dequeuePendingCashRegisterReceipt = async ({orderId, orderCode} = {}) => {
  const entries = await getPendingCashRegisterReceipts();
  const idx = findIndex(entries, {orderId, orderCode});
  if (idx < 0) return false;

  entries.splice(idx, 1);
  await writeQueue(entries);
  return true;
};

/**
 * Look up a single queued receipt (e.g. when the operator opens an order
 * and we want to show the "Re-send" button only when needed).
 */
export const getPendingCashRegisterReceiptFor = async ({orderId, orderCode} = {}) => {
  const entries = await getPendingCashRegisterReceipts();
  const idx = findIndex(entries, {orderId, orderCode});
  return idx >= 0 ? entries[idx] : null;
};

export const clearAllPendingCashRegisterReceipts = async () => {
  await AsyncStorage.removeItem(STORAGE_KEY);
  notify([]);
};
